import React, { useEffect, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import Modal from './modal';
import Utils from '../utils/utils';
import GlAccountSuggestPicker from './gl-account-suggest-picker';
import './gl-account-picker-modal.scss';

const isPresent = (value) => value !== null && value !== undefined && value !== '';

// Synced-segment fields land as {code, name} on the line item; surface the
// code (or stringify primitives) so the summary never renders [object Object].
const displayValue = (value) => {
    if (value && typeof value === 'object') {
        return value.code ?? value.Id ?? value.value ?? '';
    }
    return String(value);
};

const Stepper = ({ entries, currentIndex, fixedSet, onSelect, onValidate, t }) => {
    if (!entries || entries.length === 0) return null;
    const isLastStep = currentIndex >= entries.length - 1;
    const showNavigation = entries.length > 1;
    const allFixed = entries.every((_, idx) => fixedSet.has(idx));
    return (
        <div className="gl-picker-stepper" data-testid="gl-picker-stepper" role="tablist">
            {showNavigation && (
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={currentIndex <= 0}
                    onClick={() => onSelect(currentIndex - 1)}
                    data-testid="gl-picker-stepper-prev"
                    aria-label={t('previous')}
                >
                    {t('previous')}
                </button>
            )}
            {showNavigation && (
                <ol className="gl-picker-stepper__track">
                    {entries.map((_, idx) => (
                        <li
                            key={idx}
                            className={
                                `gl-picker-stepper__step${idx === currentIndex ? ' is-active' : ''}${fixedSet.has(idx) ? ' is-done' : ''}`
                            }
                        >
                            <button
                                type="button"
                                className="gl-picker-stepper__dot"
                                onClick={() => onSelect(idx)}
                                data-testid={`gl-picker-stepper-step-${idx}`}
                                aria-current={idx === currentIndex ? 'step' : undefined}
                                aria-label={t('invoice.gl_suggest_step_label')
                                    .replace('{index}', String(idx + 1))
                                    .replace('{total}', String(entries.length))}
                            >
                                {idx + 1}
                            </button>
                        </li>
                    ))}
                </ol>
            )}
            {!showNavigation && <span className="gl-picker-stepper__spacer" />}
            {isLastStep ? (
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={onValidate}
                    disabled={!allFixed}
                    title={!allFixed ? t('invoice.gl_suggest_validate_disabled_hint') : undefined}
                    data-testid="gl-picker-stepper-validate"
                >
                    {t('validate')}
                </button>
            ) : (
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => onSelect(currentIndex + 1)}
                    data-testid="gl-picker-stepper-next"
                >
                    {t('next')}
                </button>
            )}
        </div>
    );
};

const GlAccountPickerModal = inject('data')(({ data, entries, onApply, onValidate, onClose }) => {
    const entriesList = Array.isArray(entries) ? entries : [];

    const [currentIndex, setCurrentIndex] = useState(0);
    // Indices the user has Applied a GL combination to. Validate stays
    // disabled until every entry's index is in the set.
    const [fixedSet, setFixedSet] = useState(() => new Set());
    // Guards against double-fire when the Apply auto-validate races a
    // manual Validate click; parent remounts on validation failure.
    const validatedRef = useRef(false);

    useEffect(() => {
        if (currentIndex >= entriesList.length) {
            setCurrentIndex(0);
        }
    }, [entriesList.length, currentIndex]);

    if (entriesList.length === 0) return null;
    const safeIndex = Math.min(currentIndex, entriesList.length - 1);
    const currentEntry = entriesList[safeIndex];

    const handleValidate = () => {
        if (validatedRef.current) return;
        validatedRef.current = true;
        try {
            onValidate?.();
        } finally {
            onClose?.();
        }
    };

    const handleApply = (segmentValues) => {
        onApply?.(segmentValues, currentEntry, safeIndex);
        const nextFixed = new Set(fixedSet);
        nextFixed.add(safeIndex);
        setFixedSet(nextFixed);
        const allFixed = entriesList.every((_, idx) => nextFixed.has(idx));
        // Auto-validate the moment every line is fixed, regardless of which
        // step we're on (the user may have skipped around). Otherwise auto-
        // advance to the next entry; if we're already on the last step and
        // some earlier entry is still missing, stay put so the user can step
        // back to it.
        if (allFixed) {
            handleValidate();
        } else if (safeIndex < entriesList.length - 1) {
            setCurrentIndex(safeIndex + 1);
        }
    };

    const t = (key) => data.t.read(key);
    const isAderant = Utils.isExportFormat(data, 'aderant');
    const segCfg = data?.config?.gl_account_segment_configuration ?? {};
    const isSegActive = (segmentKey) => segCfg[segmentKey]?.active === true;
    const currentWorkItem = currentEntry.workItem ?? null;
    const lineFields = [
        { labelKey: 'invoice.description', value: currentWorkItem?.Description, alwaysShow: true },
        { labelKey: 'invoice.total', value: currentWorkItem?.Total, alwaysShow: true },
        { labelKey: 'gl_natural', value: currentWorkItem?.GLNatural, alwaysShow: true },
        { labelKey: 'invoice.gl_unit', value: currentWorkItem?.GLUnit, configKey: 'GLUnit' },
        { labelKey: 'invoice.gl_office', value: currentWorkItem?.GLOffice, configKey: 'GLOffice' },
        { labelKey: 'gl_segment.facility', value: currentWorkItem?.GLFacility, configKey: 'GLFacility' },
        { labelKey: 'invoice.gl_section', value: currentWorkItem?.GLSection, configKey: 'GLSection' },
        { labelKey: 'invoice.gl_departement', value: currentWorkItem?.GLDepartement, configKey: 'GLDepartement' },
        { labelKey: 'invoice.gl_timekeeper', value: currentWorkItem?.GLTimekeeper, configKey: 'GLTimekeeper' },
        { labelKey: 'invoice.gl_practice_group', value: currentWorkItem?.GLPracticeGroup, configKey: 'GLPracticeGroup' },
        // XPEN-3769: the Aderant line's own office and department.
        { labelKey: 'office', value: currentWorkItem?.Office, aderantOnly: true },
        { labelKey: 'department', value: currentWorkItem?.DepartmentId, aderantOnly: true },
    ].filter(({ value, alwaysShow, configKey, aderantOnly }) => (
        aderantOnly ? isAderant && isPresent(value) :
        isPresent(value) && (alwaysShow || isSegActive(configKey))
    ));

    return (
        <Modal
            title={t('invoice.gl_suggest_title')}
            width={900}
            onClose={onClose}
            secondaryAction={onClose}
            secondaryText={t('close')}
        >
            <Stepper
                entries={entriesList}
                currentIndex={safeIndex}
                fixedSet={fixedSet}
                onSelect={setCurrentIndex}
                onValidate={handleValidate}
                t={t}
            />
            {lineFields.length > 0 && (
                <div
                    className="border rounded p-3 mb-3 bg-light"
                    data-testid="gl-picker-line-summary"
                >
                    <div className="d-flex flex-wrap">
                        {lineFields.map(({ labelKey, value }) => (
                            <div key={labelKey} className="mr-4 mb-2">
                                <div className="text-muted small">{t(labelKey)}</div>
                                <div className="fw-bold">{displayValue(value)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <GlAccountSuggestPicker
                initialFilters={currentEntry.initialFilters}
                onApply={handleApply}
            />
        </Modal>
    );
});

export default GlAccountPickerModal;
